import React, { useEffect, useState } from "react";
import TableCell from "~/components/old-table-cell";
import Table from "~/components/old-table";
import TableHeader from "~/components/old-table-header";
import TableContent from "~/components/old-table-content";
import Button from "~/components/button";
import "./RateSheets.scss";
import { useBreadcrumbs } from "~/main-layout/BreadcrumbProvider";
import { useNavigate } from "react-router-dom";
import {
  viewExpenseRateSheet,
  createExpenseRateSheet,
  createTaskRateSheet,
  viewTaskRateSheet,
} from "~/routes";
import { useUser } from "~/Auth/useUser";
import _ from "lodash";
import { ExpenseRateSheet, TaskRateSheet } from "~/gql/types";
import { Body2 } from "@material/react-typography";
import { FilterOptions, RateSheet, RateSheetType } from "./types";
import {
  SearchPageWrapper,
  SearchListContent,
} from "~/components/search-page-wrapper";
import Card from "@material/react-card";
import { FilterForm } from "./Filterform";
import { SearchFilterContainer } from "~/components/search-filter-container";
import { RateSheetItem } from "./RateSheetItem";
import { filterSheets } from "./filterSheets";

type FilterRateSheetsProps = {
  rateSheets: RateSheet[];
  setItems: React.Dispatch<RateSheet[]>;
  setLoading: React.Dispatch<boolean>;
  filters: FilterOptions;
};

const FilterRateSheets: React.FC<FilterRateSheetsProps> = (props) => {
  const { setLoading, setItems, filters, rateSheets } = props;

  useEffect(() => {
    const filteredItems = filterSheets(rateSheets, filters);
    setItems(_.sortBy(filteredItems, ["name"]));
    setLoading(false);
  }, [rateSheets, filters]);

  return <></>;
};

type RateSheetsDisplayProps = {
  rateSheets: RateSheet[];
  type: RateSheetType;
  searchText: string | null;
  loading: boolean;
  assignableCount: number;
  unAssignableCount: number;
};

const RateSheetsDisplay: React.FC<RateSheetsDisplayProps> = ({
  rateSheets,
  type,
  searchText,
  loading,
  assignableCount,
  unAssignableCount,
}) => {
  const user = useUser();
  const canEdit = user.authorizations.legacyAuth.manageRateSheets;
  const navigate = useNavigate();

  useBreadcrumbs([{ text: `${type} Rate Sheets` }], []);

  const viewRoute =
    type === "Expense" ? viewExpenseRateSheet : viewTaskRateSheet;

  const createRoute =
    type === "Expense" ? createExpenseRateSheet : createTaskRateSheet;

  const editActions = (
    <Button raised primary route={createRoute}>
      <i className="material-icons mdc-button__icon">add</i>
      Create rate sheet
    </Button>
  );

  return (
    <SearchListContent
      loading={loading}
      actions={
        <div className="rate-sheet-actions">
          <Body2>{`Assignable count: ${assignableCount}`}</Body2>
          <Body2>{`Unassignable count: ${unAssignableCount}`}</Body2>
          {canEdit && editActions}
        </div>
      }
    >
      <Card className="rate-sheets-results">
        <Table>
          <div className="header">
            <TableHeader>
              <TableCell text="Name" />
              <TableCell text="Assignable?" />
              <TableCell text="Archived?" />
              <TableCell text="EndDate?" />
              <TableCell text="LastUsed?" />
            </TableHeader>
          </div>
          {rateSheets.map((rateSheet, idx) => (
            <TableContent
              key={idx}
              trailingSeparator={idx !== rateSheets.length - 1}
              onClick={() => navigate(viewRoute.toRoute(rateSheet.name).path)}
              className="rate-sheet"
            >
              <RateSheetItem
                {...{
                  rateSheet,
                  canEdit: canEdit && !rateSheet.archived,
                  type,
                  search: searchText ?? "",
                }}
              />
            </TableContent>
          ))}
        </Table>
      </Card>
    </SearchListContent>
  );
};

type RateSheetsResultsProps = {
  filters: FilterOptions;
  rateSheets: ExpenseRateSheet[] | TaskRateSheet[];
  type: RateSheetType;
};

const RateSheetsResults: React.FC<RateSheetsResultsProps> = ({
  filters,
  rateSheets,
  type,
}) => {
  const [items, setItems] = useState<RateSheet[]>([]);
  const [loading, setLoading] = useState(true);

  const allSheets = rateSheets as RateSheet[];

  const assignableCount = rateSheets.filter((x) => x.allowAssignment).length;
  const unAssignableCount = rateSheets.filter((x) => !x.allowAssignment).length;

  return (
    <>
      <FilterRateSheets
        rateSheets={allSheets}
        setItems={setItems}
        filters={filters}
        setLoading={setLoading}
      />
      <RateSheetsDisplay
        rateSheets={items}
        type={type}
        searchText={filters.searchText}
        loading={loading}
        assignableCount={assignableCount}
        unAssignableCount={unAssignableCount}
      />
    </>
  );
};

type RateSheetsProps = {
  rateSheets: ExpenseRateSheet[] | TaskRateSheet[];
  type: RateSheetType;
  FilterFormComponent?: typeof FilterForm;
};

export const RateSheetsSearch: React.FC<RateSheetsProps> = ({
  rateSheets,
  type,
  FilterFormComponent,
}) => {
  const [filters, setFilters] = useState<FilterOptions | null>(null);

  const FilterComponent = FilterFormComponent || FilterForm;
  const setFilterOptions = (opts: FilterOptions) => setFilters(opts);

  return (
    <SearchPageWrapper className="rate-sheets">
      <SearchFilterContainer>
        <FilterComponent onFiltersChanged={setFilterOptions} />
      </SearchFilterContainer>
      {filters && (
        <RateSheetsResults
          filters={filters}
          rateSheets={rateSheets}
          type={type}
        />
      )}
    </SearchPageWrapper>
  );
};
